/**
 * RENDA-K のプリセットペイロード（直列化・検証）。
 *
 * ツールプリセット基盤（`useToolPresetBrowser` + `/api/me/tools/.../presets`）に
 * 保存する JSON の組み立てと、読み込み時のサニタイズを担う。
 *
 * ソース音源と連打開始位置（startSec）はプリセットに含めない。
 * プリセットは「打ち方」の設定のみで、音源は都度読み込む前提。
 *
 * サニタイズ方針は phrase-bloom と同じ「読めるところは読む」:
 *  - 各フィールドをデフォルト値とマージし、数値はレンジにクランプする
 *  - 古い/未来のプリセットを読んでもツールが壊れないことを保証する
 *  - ペイロード全体がオブジェクトでない場合のみ null（読込失敗）を返す
 */

import {
  DEFAULT_RENDA_PARAMS,
  STEP_MAX,
  STEP_MIN,
  type RendaParams,
} from "./stutterPlan";

export const RENDA_K_TOOL_ID = "renda-k";

/** ペイロード形式のバージョン。互換の無い構造変更をしたら上げる。 */
export const RENDA_K_SCHEMA_VERSION = 1;

/** プリセットに保存する設定（音源・開始位置は含まない） */
export type RendaPresetParams = Omit<RendaParams, "startSec">;

export interface RendaKPresetPayload {
  params: RendaPresetParams;
}

/**
 * 現在のツール状態をプリセットペイロードに直列化する（startSec は除外）。
 * キーの並び順は sanitizeParams と揃えること（プリセットバーのダーティ
 * 判定が JSON 文字列比較のため）。
 */
export const buildPresetPayload = (
  params: RendaParams,
): RendaKPresetPayload => ({
  params: {
    bpm: params.bpm,
    lengthSixteenths: params.lengthSixteenths,
    intervalStartStep: params.intervalStartStep,
    intervalEndStep: params.intervalEndStep,
    curveAmount: params.curveAmount,
    quantize: params.quantize,
    speed: params.speed,
    accel: params.accel,
  },
});

// ===== サニタイズ用プリミティブ =====

const clampNumber = (
  value: unknown,
  fallback: number,
  min: number,
  max: number,
): number => {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return Math.round(Math.min(max, Math.max(min, value)));
};

const asBoolean = (value: unknown, fallback: boolean): boolean =>
  typeof value === "boolean" ? value : fallback;

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const sanitizeParams = (raw: unknown): RendaPresetParams => {
  const r = asRecord(raw);
  const d = DEFAULT_RENDA_PARAMS;
  return {
    bpm: clampNumber(r.bpm, d.bpm, 60, 240),
    lengthSixteenths: clampNumber(
      r.lengthSixteenths,
      d.lengthSixteenths,
      1,
      16,
    ),
    intervalStartStep: clampNumber(
      r.intervalStartStep,
      d.intervalStartStep,
      STEP_MIN,
      STEP_MAX,
    ),
    intervalEndStep: clampNumber(
      r.intervalEndStep,
      d.intervalEndStep,
      STEP_MIN,
      STEP_MAX,
    ),
    curveAmount: clampNumber(r.curveAmount, d.curveAmount, -100, 100),
    quantize: asBoolean(r.quantize, d.quantize),
    speed: clampNumber(r.speed, d.speed, 0, 100),
    accel: clampNumber(r.accel, d.accel, 0, 100),
  };
};

/**
 * 保存済みペイロードを検証し、安全な `RendaKPresetPayload` に正規化する。
 * ペイロード全体がオブジェクトでない場合のみ null（読込失敗）。
 * スキーマは現状 v1 のみ。互換の無い構造変更をしたら schema_version 引数を
 * 受けて分岐を追加する。
 */
export const sanitizePresetPayload = (
  raw: unknown,
): RendaKPresetPayload | null => {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return null;
  }
  return { params: sanitizeParams((raw as Record<string, unknown>).params) };
};
